<template>
  <div class="page-container">
    <header class="page-header">
      <div class="d-flex align-items-center">
        <portal-target name="sidebar-toggle" class="d-flex align-items-center mt-1"/>
        <h4 class="mb-0">{{ title }}</h4>
      </div>
      <div class="d-flex align-items-center">
        <b-button id="table-settings" class="custom-btn-secondary mr-1" v-b-toggle.sidebar-fields>
          <v-icon name="buttons/cog" :size="13"/>
        </b-button>
        <b-tooltip target="table-settings" triggers="hover" custom-class="custom-tooltip" noninteractive>
          Колонки
        </b-tooltip>
      </div>
    </header>

    <div class="page-body">
      <div class="page-filters">
        <filters-drawer
          :query.sync="filters.query"
          :is-busy="isBusy"
          :filter-tags="activeFilterTags"
          :disabled-submit="isStartDateEmpty"
          @remove-tag="removeFilterTag"
          @submit="onSearch"
          @clear="onClearFilters"
        >
          <b-col cols="12">
            <label class="small text-muted">Период</label>
            <b-row class="align-items-center">
              <b-col>
                <b-form-input
                  id="input_start_date"
                  size="sm"
                  type="date"
                  v-model.date="filters.start_date"
                  :state="isStartDateEmpty ? false : null"
                />
              </b-col>
              <b-col cols="auto" class="px-2">
                <span>&mdash;</span>
              </b-col>
              <b-col>
                <b-form-input
                  id="input_end_date"
                  size="sm"
                  type="date"
                  v-model.date="filters.end_date"
                />
              </b-col>
            </b-row>
          </b-col>
          <b-col v-if="isStartDateEmpty">
            <small class="text-danger">Укажите дату начала периода!</small>
          </b-col>
          <b-col cols="12" class="mt-1">
            <label class="small text-muted">Сотрудники</label>
            <b-form-select
              size="sm"
              v-if="canReadOther()"
              id="input_executors"
              v-model="filters.user_id">
              <option :value="0">Все сотрудники</option>
              <option v-for="option in activeExecutors" :value="option.id">
                {{ option.name }}
              </option>
            </b-form-select>
          </b-col>
        </filters-drawer>
      </div>

      <div class="page-table-container">
        <b-table
          id="manager-stat"
          ref="manager_stat"
          class="custom-table mb-0 text-small"
          sticky-header
          selectable
          select-mode="single"
          responsive
          show-empty
          :empty-text="$t('message.common.emptyTable')"
          :busy="isBusy"
          :items="items"
          :fields="renderedFields"
          :current-page="currentPage"
          :per-page="perPage"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :filter="filters.query"
          @filtered="onFiltered"
        >
          <template #table-busy>
            <tables-skeleton :fields="renderedFields" :rows="6"/>
          </template>

          <template #cell(payment_type)="row">
            {{ paymentTypeString(row.item.payment_type_int) }}
          </template>

          <template #cell(start_date)="row">
            {{ formatTableDateTime(row.item.start_date) }}
          </template>

          <template #cell(total_hours)="row">
            {{ priceFormat(row.item.total_hours) }} {{ getStatsUnitShortName(row.item.unit_code) }}
          </template>

          <template #cell(total_delta)="row">
            {{ roundDelta(row.item.total_delta) }}
          </template>

          <template #cell(total_manager)="row">
            {{ roundDelta(row.item.total_manager) }}
          </template>

          <template #cell(paid)="row">
            {{ paid(row) }}
          </template>

          <template #cell(read_only)="row">
            {{ readOnly(row) }}
          </template>
        </b-table>
      </div>

      <div class="page-pagination">
        <pagination-footer
          use-router
          :current-page.sync="currentPage"
          :total-rows="totalRows"
          :per-page.sync="perPage"
          :page-options="pageOptions"
          @page-click="onPagination"
          @per-page-change="onPerPageChange"
        >
          <template #footer-list>
            <div>
              Заявок: <b>{{totalRows}}</b> |
              Кол-во: <b>{{ unitTotalsText }}</b> |
              С заказчика: <b>{{ priceFormat(totalCustomer) }}</b> |
              На руки: <b>{{ priceFormat(totalManager) }}</b> |
              Оплачено заказчиком: <b>{{ priceFormat(totalCustomerPaid) }}</b> |
              Получить на руки: <b>{{ priceFormat(totalManagerPaid) }}</b>
            </div>
          </template>
        </pagination-footer>
      </div>
    </div>

    <DynamicTables
      storage-key="managerStatistic_columns"
      sidebar-fields="sidebar-fields"
      :fields-model.sync="fields"
      fieldKey="id"
    />
  </div>
</template>

<script>
import statistics from "../../network/statistics";
import users from "../../network/users";
import unitsStore from "../../utilites/unitStore";
  import dateFormatter from '../../utilites/dateFormatter'
  import helpers from "../../utilites/helpers";
  import DynamicTables from "../../utilites/commonUi/DynamicTables.vue";
  import FiltersDrawer from "../../utilites/commonUi/FiltersDrawer.vue";
  import permissions, {PERM} from "../../utilites/permissions";
  import pageSearchService from "../../utilites/pageSearchService";
  import PaginationFooter from "../../utilites/commonUi/PaginationFooter.vue";
  import TablesSkeleton from "../../utilites/commonUi/TablesSkeleton.vue";

export default {
  name: 'ManagerStatistics',
  components: { TablesSkeleton, PaginationFooter, FiltersDrawer, DynamicTables },
  mixins: [
    statistics, users,
    dateFormatter, helpers, permissions, pageSearchService, unitsStore
  ],
  data() {
    return {
      title: 'Статистика по менеджерам',
      formUrl: 'statistics/tasks',
      sortBy: 'id',
      sortDesc: false,
      items: [],
      fields: [
        {
          key: 'id',
          label: 'id',
          sortable: true,
          visible: true,
        },
        {
          key: 'customer_name',
          label: this.$t('message.tasks.table.customer'),
          sortable: true,
          visible: true,
        },
        {
          key: 'start_address',
          label: this.$t('message.tasks.table.start_address'),
          sortable: true,
          visible: true,
        },
        {
          key: 'start_date',
          label: this.$t('message.tasks.table.start_date'),
          sortable: true,
          class: 'widthDate',
          visible: true,
        },
        {
          key: 'payment_type',
          label: this.$t('message.tasks.table.payment_type'),
          sortable: true,
          visible: true,
        },
        {
          key: 'total_hours',
          label: 'Кол-во',
          sortable: true,
          visible: true,
        },
        {
          key: 'total_price',
          label: this.$t('message.tasks.table.total_price'),
          sortable: true,
          visible: true,
        },
        {
          key: 'total_delta',
          label: this.$t('message.tasks.table.total_delta'),
          sortable: true,
          visible: true,
        },
        {
          key: 'total_manager',
          label: "Менеджеру",
          sortable: true,
          visible: true,
        },
        {
          key: 'paid',
          label: this.$t('message.tasks.table.paid'),
          sortable: true,
          visible: true,
        },
        {
          key: 'executor_name',
          label: 'Сотрудник',
          sortable: true,
          visible: true,
        },
        {
          key: 'read_only',
          label: this.$t('message.tasks.table.read_only'),
          sortable: true,
          visible: true,
        },
      ],
      isReadOnly: false,
      isPaid: false,
      pageOptions: [50, 100, 500, { value: 10000, text: 'Все'}],
      isBusy: false,
      totalRows: 0,
      unitTotalsText: "",
      totalDelta: 0,
      totalManager: 0,
      totalManagerPaid: 0,
      totalCustomer: 0,
      totalCustomerPaid: 0,
      executors: [],
      filters: this.getDefaultFilters(),
      user: {},
      usersUrl: 'users'
    }
  },
  async created() {
    this.isBusy = true;
    const query = this.$route.query;

    if (query) {
      this.filters.start_date = query.start_date ? query.start_date : this.getStartDateFilter();

      this.filters.end_date = query.end_date ? query.end_date : this.getEndDateFilter();

      if (query.user_id) {
        this.filters.user_id = query.user_id;
      }
    }

    await this.getExecutors();
    await this.onSearch();
  },
  computed: {
    activeExecutors() {
      return this.executors.filter(item => item.active)
    },
    visibleFields() {
      return this.fields.filter(field => field.visible);
    },
    renderedFields() {
      return [...this.visibleFields];
    },
    activeFilterTags() {
      const tags = [];
      const defaults = this.getDefaultFilters();
      const start = this.filters.start_date;
      const end = this.filters.end_date;

      if (start || end) {
        let dateTag = '';

        if (start && end) {
          dateTag = `${this.formatTableDate(start)} - ${this.formatTableDate(end)}`;
        } else if (start) {
          dateTag = `с ${this.formatTableDate(start)}`;
        } else {
          dateTag = `по ${this.formatTableDate(end)}`;
        }
        tags.push({ key: 'date', label: dateTag });
      }

      if (this.filters.user_id !== defaults.user_id) {
        const foundItem = this.activeExecutors.find(s => s.id === this.filters.user_id);
        tags.push({ key: 'user', label: foundItem ? foundItem.name : 'Сотрудник'});
      }

      return tags;
    },
    isStartDateEmpty() {
      return !this.filters.start_date;
    },
  },
  methods: {
    async getItems() {

      this.isBusy = true;
      this.clearTotals();

      const data = {
        start_date: this.filters.start_date,
        end_date: this.filters.end_date,
        user_id: this.filters.user_id,
      }

      const result = await this.getManagerStatistics(data);

      if (!result || !result.data) {
        this.items = [];
        this.totalRows = this.items.length;
        this.isBusy = false;
        return;
      }

      this.items = result.data;
      this.updateTotals(this.items);
      this.isBusy = false;
    },
    // Filters
    getDefaultFilters() {
      let filters = {
        query: '',
        start_date: null,
        end_date: null,
        user_id: 0,
      };

      filters.start_date = this.getStartDateFilter();
      filters.end_date = this.getEndDateFilter();
      return filters;
    },
    onClearFilters() {
      const filters = this.getDefaultFilters();

      if (JSON.stringify(this.filters) === JSON.stringify(filters)) return;

      this.filters = filters;
      this.onSearch();
    },
    getStartDateFilter() {
      let date = new Date();
      date.setDate(date.getDate() - 15);
      return this.formatDate(date);
    },
    getEndDateFilter() {
      return this.formatDate(new Date());
    },
    onFiltered(filteredItems) {
      this.currentPage = 1;
      this.updateTotals(filteredItems);
    },
    removeFilterTag(tag) {
      if (tag.key === 'date') {
        this.filters.start_date = this.getStartDateFilter();
        this.filters.end_date = null;
      }
      else if (tag.key === 'user') {
        this.filters.user_id = 0;
      }
      this.onSearch();
    },
    len(arr) {
      return arr ? arr.length : 0;
    },
    paid(row) {
      return this.yesOrNoText(row.item.paid);
    },
    priceFormat(value) {
      return new Intl.NumberFormat('ru-RU').format(value);
    },
    clearTotals() {
      this.totalRows = 0;
      this.unitTotalsText = "";
      this.totalDelta = 0;
      this.totalManager = 0;
      this.totalManagerPaid = 0;
      this.totalCustomer = 0;
      this.totalCustomerPaid = 0;
    },
    updateTotals(items) {
      const totals = this.calculateTotals(items);

      this.totalRows = this.len(items);
      this.unitTotalsText = this.formatStatsUnitTotals(items);
      this.totalDelta = totals.totalDelta;
      this.totalManager = totals.totalManager;
      this.totalCustomerPaid = totals.totalCustomerPaid;
      this.totalManagerPaid = totals.totalManagerPaid;
      this.totalCustomer = totals.totalCustomer;
    },
    calculateTotals(tasks) {
      let delta = 0;
      let manager = 0;
      let customerPaid = 0;
      let managerPaid = 0;
      let customer = 0;

      tasks.forEach(function (item) {
        const totalDeltaSum = delta + item.total_delta;
        delta = Math.round(totalDeltaSum * 100) / 100;

        const totalManagerSum = manager + item.total_manager;
        manager = Math.round(totalManagerSum * 100) / 100;

        if (item.paid) {
          const customerPaidSum = customerPaid + item.total_price;
          customerPaid = Math.round(customerPaidSum * 100) / 100;

          const managerPaidSum = managerPaid + item.total_manager;
          managerPaid = Math.round(managerPaidSum * 100) / 100;
        }
        const totalCustomerSum = customer + item.total_price;
        customer = Math.round(totalCustomerSum * 100) / 100;
      })

      return {
        totalDelta: delta,
        totalManager: manager,
        totalCustomerPaid: customerPaid,
        totalManagerPaid: managerPaid,
        totalCustomer: customer,
      }
    },
    roundDelta(item) {
      return Math.round(item * 100) / 100;
    },
    async getExecutors() {
      const result = await this.getUsers();

      if (!result || !result.data) {
        this.executors = [];
        return;
      }

      this.executors = result.data.users;
    },
    readOnly(row) {
      return this.yesOrNoText(!row.item.read_only);
    },
    canReadOther() {
      return this.hasPermission([PERM.STATISTIC_READ_OTHER]);
    },
  },
}
</script>

<style>
#manager-stat-pagination {
  margin-bottom: 0;
}

.widthDate {
  width: 170px;
}
</style>



// WEBPACK FOOTER //
// src/components/Statistics/ManagerStatistic.vue